// Canonical product fields the Catalog Migration Wizard can import into,
// shared by the column-mapping auto-suggestion, the CSV template download,
// and the on-screen column reference. Keys must match the backend's
// services/product_import.py CANONICAL_FIELDS exactly.
export const IMPORT_FIELDS = [
  { key: 'name', label: 'Product Name', required: true, type: 'text',
    hint: 'The product/brand name', aliases: ['name', 'product', 'product name', 'item', 'item name', 'title'],
    sample: 'Aspirin 100mg' },
  { key: 'unit_price', label: 'Selling Price', required: true, type: 'number',
    hint: 'Price charged to customers', aliases: ['unit price', 'price', 'selling price', 'sale price', 'retail price'],
    sample: '3.50' },
  { key: 'generic_name', label: 'Generic Name', type: 'text',
    hint: 'Generic/chemical name — left blank, AI will try to fill it in', aliases: ['generic name', 'generic', 'chemical name', 'active ingredient'],
    sample: 'Acetylsalicylic Acid' },
  { key: 'category', label: 'Category', type: 'text',
    hint: 'Default: General', aliases: ['category', 'product category', 'type'],
    sample: 'Pain Relief' },
  { key: 'manufacturer', label: 'Manufacturer', type: 'text',
    hint: 'Brand manufacturer', aliases: ['manufacturer', 'brand', 'maker', 'manufacturer name'],
    sample: 'Bayer' },
  { key: 'unit_cost', label: 'Unit Cost', type: 'number',
    hint: 'Cost/purchase price', aliases: ['unit cost', 'cost', 'cost price', 'purchase price', 'buying price'],
    sample: '1.80' },
  { key: 'stock_quantity', label: 'Opening Stock', type: 'number',
    hint: 'Opening stock count', aliases: ['stock quantity', 'stock', 'quantity', 'qty', 'opening stock', 'stock qty'],
    sample: '200' },
  { key: 'min_stock_level', label: 'Minimum Stock Level', type: 'number',
    hint: 'Reorder threshold (default: 10)', aliases: ['min stock level', 'min stock', 'reorder level', 'reorder point', 'minimum stock'],
    sample: '30' },
  { key: 'preferred_supplier', label: 'Preferred Supplier', type: 'text',
    hint: 'Supplier name', aliases: ['preferred supplier', 'supplier', 'vendor', 'supplier name'],
    sample: 'PharmaCorp Ltd' },
  { key: 'requires_prescription', label: 'Requires Prescription', type: 'boolean',
    hint: 'true/false (default: false)', aliases: ['requires prescription', 'rx', 'prescription', 'prescription required'],
    sample: 'false' },
  { key: 'is_controlled', label: 'Controlled Drug (Narcotic)', type: 'boolean',
    hint: 'true/false (default: false)', aliases: ['is controlled', 'controlled', 'narcotic', 'controlled drug'],
    sample: 'false' },
  { key: 'is_otc', label: 'OTC (offered in the patient shop)', type: 'boolean',
    hint: 'true/false (default: false)', aliases: ['is otc', 'otc', 'over the counter', 'patient shop'],
    sample: 'false' },
  { key: 'is_active', label: 'Active Product', type: 'boolean',
    hint: 'true/false (default: true)', aliases: ['is active', 'active', 'status'],
    sample: 'true' },
  { key: 'allow_open_sale', label: 'Allow Open-Box (Loose Unit) Sale', type: 'boolean',
    hint: 'true/false (default: false)', aliases: ['allow open sale', 'open box', 'open-box sale', 'loose unit', 'loose sale'],
    sample: 'false' },
  { key: 'description', label: 'Description', type: 'text',
    hint: 'Product notes', aliases: ['description', 'notes', 'details'],
    sample: 'Low dose aspirin' },
  { key: 'barcode', label: 'Barcode / SKU', type: 'text',
    hint: 'UPC/EAN barcode', aliases: ['barcode', 'sku', 'upc', 'ean', 'code'],
    sample: '8901234567890' },
  { key: 'batch_number', label: 'Opening Batch Number', type: 'text',
    hint: 'Optional — leave blank for plain opening stock with no batch', aliases: ['batch number', 'batch', 'batch no', 'lot number', 'lot'],
    sample: '' },
  { key: 'expiry_date', label: 'Opening Batch Expiry Date', type: 'text',
    hint: 'Optional — format YYYY-MM-DD', aliases: ['expiry date', 'expiry', 'expiration date', 'exp date', 'expires'],
    sample: '' },
];

export const REQUIRED_FIELD_KEYS = IMPORT_FIELDS.filter(f => f.required).map(f => f.key);

const normalize = (s) => (s || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');

function headerScore(header, field) {
  const h = normalize(header);
  if (!h) return 0;
  let best = 0;
  for (const alias of [field.key, field.label, ...field.aliases]) {
    const a = normalize(alias);
    if (!a) continue;
    if (h === a) return 100;
    if (h.includes(a) || a.includes(h)) {
      const overlap = Math.min(a.length, h.length) / Math.max(a.length, h.length);
      best = Math.max(best, 70 * overlap);
    }
  }
  return best;
}

/**
 * Auto-suggests a canonical field for each CSV header by name similarity.
 * Returns { [header]: fieldKey | null } — one header maps to at most one
 * field, and one field is claimed by at most one header (greedy, highest
 * score first).
 */
export function suggestMapping(headers, fields = IMPORT_FIELDS) {
  const candidates = [];
  headers.forEach((header) => {
    fields.forEach((field) => {
      const score = headerScore(header, field);
      if (score >= 40) candidates.push({ header, fieldKey: field.key, score });
    });
  });
  candidates.sort((a, b) => b.score - a.score);

  const mapping = {};
  headers.forEach((h) => { mapping[h] = null; });
  const usedFields = new Set();
  for (const c of candidates) {
    if (mapping[c.header] !== null) continue;
    if (usedFields.has(c.fieldKey)) continue;
    mapping[c.header] = c.fieldKey;
    usedFields.add(c.fieldKey);
  }
  return mapping;
}

export function buildTemplateCsv(fields = IMPORT_FIELDS) {
  const headers = fields.map(f => f.key).join(',');
  const sample = fields.map(f => f.sample).join(',');
  return `${headers}\n${sample}\n`;
}

export function downloadTemplate(fields = IMPORT_FIELDS, kind = 'product') {
  const blob = new Blob([buildTemplateCsv(fields)], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `dutilyne-${kind}-import-template.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Applies the chosen header->field mapping to the parsed CSV rows, producing
 * rows keyed by canonical field name (raw string values) ready to send to
 * the backend preview/execute endpoints.
 */
export function applyMapping(rows, mapping) {
  const entries = Object.entries(mapping).filter(([, fieldKey]) => fieldKey);
  return rows.map((row) => {
    const mapped = {};
    entries.forEach(([header, fieldKey]) => {
      mapped[fieldKey] = row[header] != null ? String(row[header]) : '';
    });
    return mapped;
  });
}
